import React, { useCallback, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { IButtonProps, IButtonStyles, IStackProps, IStackTokens, Link, ProgressIndicator, Stack } from '@fluentui/react';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import { ActionButton as CommonActionButton } from 'common/components/Buttons/v8';
import { Panel } from 'common/components/Panel';
import { Heading3, Text } from 'common/components/Text';
import { isSecureConnectionApiEnabled, isTrustedNetworkApiEnabled } from 'common/utilities/FlightSetting';
import { pageDataService } from 'common/utilities/PageDataService';
import { logClick, useLogViewOnMount } from 'common/utilities/telemetryHelpers';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { isEnglishUSLocale, useLocalizedStrings } from 'i18n';
import {
  Alert,
  BreachedDetail,
  CreditAlert,
  DevicesGetDevicesSummaryOptionalParams,
  DeviceSummary,
  TrustedNetwork,
} from 'mcsclient/models';
import { useNotifications } from 'store/layout/hooks/useNotifications';

import { RestError } from '@azure/core-http';

import { IOfficeShellPanelProps } from './OfficeShellPanel.types';

const myProfileUrl = 'https://go.microsoft.com/fwlink/?linkid=2184908';
const handleYourDataUrl = 'https://go.microsoft.com/fwlink/p/?linkid=2207622';
const consumerHealthPrivacyPolicyUrl = 'https://go.microsoft.com/fwlink/?linkid=2259814';

const containerTokens: IStackTokens = { childrenGap: 32 };

const sectionTokens: IStackTokens = { childrenGap: 16 };
const Section = (props: IStackProps) => <Stack {...props} tokens={sectionTokens} />;

const actionButtonStyles: Partial<IButtonStyles> = {
  root: { minHeight: 'initial !important' },
  flexContainer: { padding: '6px 0' },
  label: { textAlign: 'left' },
};
const ActionButton = (props: IButtonProps) => <CommonActionButton {...props} styles={actionButtonStyles} />;

const disabledApiExceptionCodes: (string | undefined)[] = [
  '719', // AlertsCenterFeatureRequiredButNotEnabledExceptionCode
  // No feature flag for device API (DevicesController.GetDevicesSummary)
  '710', // IdentityTheftMonitoringApiFeatureRequiredButNotEnabledFeatureCode
  '737', // CreditMonitoringApiFeatureRequiredButNotEnabledFeatureCode
  '746', // OneTimeCyberAgentScanApiFeatureRequiredButNotEnabledFeatureCode
  '739', // SecureConnectionApiFeatureRequiredButNotEnabledFeaureCode
  '753', // TrustedNetworkApiFeatureRequiredButNotEnabledFeatureCode
];

const isDisabledApiException = (error: unknown): boolean => {
  return error instanceof RestError && disabledApiExceptionCodes.includes(error.code);
};

const PrivacySettings: React.FC = () => {
  const { t } = useLocalizedStrings();

  const [isExporting, setIsExporting] = useState(false);
  const [percentComplete, setPercentComplete] = React.useState(0);
  const { toastGenericError } = useNotifications();

  useLogViewOnMount('PrivacyPage');

  const dsrExport = useCallback(async () => {
    const exportStartTime = Date.now();
    setIsExporting(true);
    setPercentComplete(0);
    let previousApiCallSucceeded = true;
    const allAlerts: Alert[] = [];
    let devices: DeviceSummary[] = [];
    let deletedDevices: DeviceSummary[] = [];
    let breaches: BreachedDetail[] = [];
    let creditReport;
    let creditAlerts: CreditAlert[] = [];
    let oneTimeScanStatus;
    let oneTimeScanBreaches: BreachedDetail[] = [];
    let secureConnectionBandwidthUsageReport;
    let trustedNetworks: TrustedNetwork[] = [];
    let profile;
    let currentPageNumber = 1;
    let hasMoreData = true;
    let hitAlertsMaxLimit = false;
    const isSecureConnectionApiEnabledFlag: boolean = isSecureConnectionApiEnabled();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const replacer = (_key: string, value: any) => {
      if (value === null) {
        return undefined;
      }
      return value;
    };

    while (hasMoreData && previousApiCallSucceeded) {
      // eslint-disable-next-line no-await-in-loop
      await microsoftConsumerSecurityClient.alerts
        .getAlerts({ includeFamily: false, pageSize: 20, pageNumber: currentPageNumber })
        // eslint-disable-next-line no-loop-func
        .then((payload) => {
          const { alerts, paginationMetadata } = payload;
          if (alerts) {
            allAlerts.push(...alerts);
          }
          const { totalPageCount = 1 } = paginationMetadata || {};
          if (totalPageCount <= currentPageNumber) {
            hasMoreData = false;
          }

          if (allAlerts.length > 0) {
            setPercentComplete(0.4 * (currentPageNumber / totalPageCount));
          }
          currentPageNumber += 1;
          if (currentPageNumber > 50) {
            // hard cap
            hasMoreData = false;
            hitAlertsMaxLimit = true;
          }
        })
        // eslint-disable-next-line no-loop-func
        .catch((error: unknown) => {
          hasMoreData = false;
          if (!isDisabledApiException(error)) {
            previousApiCallSucceeded = false;
            toastGenericError(error, 'exportGetAlertsFailed');
          }
        });
    }

    if (previousApiCallSucceeded) {
      const params: DevicesGetDevicesSummaryOptionalParams = {
        sortBy: 'Severity',
        sortOrder: 'Desc',
        includeFamily: false,
        includeDeletedDevices: true,
      };
      await microsoftConsumerSecurityClient.devices
        .getDevicesSummary(params)
        .then((payload) => {
          devices = payload.devices || [];
          deletedDevices = payload.deletedDevices || [];
        })
        .catch((error: unknown) => {
          if (!isDisabledApiException(error)) {
            previousApiCallSucceeded = false;
            toastGenericError(error, 'exportGetDevicesSummaryFailed');
          }
        });
      setPercentComplete(0.5);
    }

    const isItmOnboarded = pageDataService.isITPOnboarded();
    const isCmOnboarded = pageDataService.isCMOnboarded();
    if (isItmOnboarded) {
      if (previousApiCallSucceeded) {
        profile = await microsoftConsumerSecurityClient.identityTheftMonitoringSubscription
          .getCompleteSubscriberProfile()
          .catch((error: unknown) => {
            if (!isDisabledApiException(error)) {
              previousApiCallSucceeded = false;
              toastGenericError(error, 'exportGetCompleteSubscriberProfileFailed');
            }
          });
        setPercentComplete(0.6);
      }

      if (previousApiCallSucceeded) {
        await microsoftConsumerSecurityClient.breaches
          .getBreaches()
          .then((payload) => {
            breaches = payload || [];
          })
          .catch((error: unknown) => {
            if (!isDisabledApiException(error)) {
              previousApiCallSucceeded = false;
              toastGenericError(error, 'exportGetBreachesFailed');
            }
          });
        setPercentComplete(0.7);
      }

      if (previousApiCallSucceeded && isCmOnboarded) {
        await microsoftConsumerSecurityClient.credit
          .getCreditSummary()
          .then((payload) => {
            creditAlerts = payload.alerts || [];
            creditReport = payload.report;
          })
          .catch((error: unknown) => {
            if (!isDisabledApiException(error)) {
              previousApiCallSucceeded = false;
              toastGenericError(error, 'exportGetCreditSummaryFailed');
            }
          });
        setPercentComplete(0.8);
      }
    }

    const hasUsedOneTimeScan = pageDataService.hasUsedOneTimeScan();
    if (previousApiCallSucceeded && hasUsedOneTimeScan) {
      await microsoftConsumerSecurityClient.oneTimeCyberAgentScan
        .getStatusData()
        .then((payload) => {
          oneTimeScanStatus = payload;
        })
        .catch((error: unknown) => {
          if (!isDisabledApiException(error)) {
            previousApiCallSucceeded = false;
            toastGenericError(error, 'exportGetOneTimeScanStatusFailed');
          }
        });
      // Same percent complete as CM
      setPercentComplete(0.8);
    }

    const hasActiveOneTimeScanResult = pageDataService.hasActiveOneTimeScanResult();
    if (previousApiCallSucceeded && hasActiveOneTimeScanResult) {
      await microsoftConsumerSecurityClient.oneTimeCyberAgentScan
        .getExistingBreaches()
        .then((payload) => {
          oneTimeScanBreaches = payload || [];
        })
        .catch((error: unknown) => {
          if (!isDisabledApiException(error)) {
            previousApiCallSucceeded = false;
            toastGenericError(error, 'exportGetOneTimeScanBreachesFailed');
          }
        });
      setPercentComplete(0.85);
    }

    if (previousApiCallSucceeded && isSecureConnectionApiEnabledFlag) {
      await microsoftConsumerSecurityClient.secureConnection
        .getBandwidthUsage()
        .then((payload) => {
          secureConnectionBandwidthUsageReport = payload || [];
        })
        .catch((error: unknown) => {
          if (!isDisabledApiException(error)) {
            previousApiCallSucceeded = false;
            toastGenericError(error, 'exportGetSecureConnectionBandwidthUsageFailed');
          }
        });
      setPercentComplete(0.9);
    }

    if (previousApiCallSucceeded && isTrustedNetworkApiEnabled()) {
      await microsoftConsumerSecurityClient.trustedNetwork
        .getTrustedNetworks()
        .then((payload) => {
          trustedNetworks = payload || [];
        })
        .catch((error: unknown) => {
          if (!isDisabledApiException(error)) {
            previousApiCallSucceeded = false;
            toastGenericError(error, 'exportGetTrustedNetworksFailed');
          }
        });
      setPercentComplete(0.95);
    }

    if (previousApiCallSucceeded) {
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const data = {
          alerts: allAlerts,
          devices,
          deletedDevices,
          ...(isItmOnboarded && { identityTheftMonitoring: { profile, breaches } }),
          ...(isCmOnboarded && { creditMonitoring: { creditAlerts, creditReport } }),
          ...(hasActiveOneTimeScanResult && { oneTimeScan: { oneTimeScanBreaches } }),
          ...(hasUsedOneTimeScan && { oneTimeScanStatus }),
          ...(isSecureConnectionApiEnabledFlag && { secureConnectionBandwidthUsage: { secureConnectionBandwidthUsageReport } }),
          trustedNetworks,
        };

        const json = JSON.stringify(data, replacer, 2);
        const blob = new Blob([json], { type: 'octet/stream' });
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `mydata-${new Date().toISOString()}.txt`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setPercentComplete(1);

        const loadTime = Date.now() - exportStartTime;
        TelemetryProvider.Instance.trackEvent('DsrExport', {
          downloadTime: loadTime.toString(),
          alertCount: allAlerts.length,
          deviceCount: devices.length,
          deletedDeviceCount: deletedDevices.length,
          breachCount: breaches.length,
          creditAlertsCount: creditAlerts.length,
          isItmOnboarded,
          hitAlertsMaxLimit,
        });
      }
    }

    setIsExporting(false);
  }, [toastGenericError]);

  const manageMyInfoSection = useMemo(
    () => (
      <Section>
        <Heading3 variant="Subtitle2">{t('OfficeShellPanel.PrivacySettings.DataPrivacy')}</Heading3>
        <Text variant="Caption2">{t('OfficeShellPanel.PrivacySettings.DataPrivacyExplain')}</Text>
        <ActionButton
          href={myProfileUrl}
          target="_blank"
          onClick={logClick('DataPrivacyClicks', () => {})}
          aria-label={t('OfficeShellPanel.PrivacySettings.ManageMyInfoAriaLabel')}
        >
          {t('OfficeShellPanel.PrivacySettings.ManageMyInfo')}
        </ActionButton>
      </Section>
    ),
    [t]
  );

  const downloadMyDataSection = useMemo(() => {
    return (
      <Section>
        <Heading3 variant="Subtitle2">{t('OfficeShellPanel.PrivacySettings.DefenderData')}</Heading3>
        <Stack.Item>
          <Text variant="Caption2">
            <Trans
              i18nKey="OfficeShellPanel.PrivacySettings.DefenderDataExplain"
              components={{
                handleYourDataLink: (
                  <Link
                    href={handleYourDataUrl}
                    target="_blank"
                    aria-label={t('OfficeShellPanel.PrivacySettings.DefenderDataExplainAriaLabel')}
                  />
                ),
              }}
              t={t}
            />
          </Text>
        </Stack.Item>
        {isExporting ? (
          <ProgressIndicator label="Downloading" percentComplete={percentComplete} />
        ) : (
          <ActionButton
            text={t('OfficeShellPanel.PrivacySettings.DownloadMyData')}
            onClick={logClick(`DownloadYourData`, dsrExport)}
          />
        )}
      </Section>
    );
  }, [dsrExport, isExporting, percentComplete, t]);

  const consumerHealthPrivacyPolicySection = useMemo(
    () => (
      <Section>
        <ActionButton
          href={consumerHealthPrivacyPolicyUrl}
          target="_blank"
          onClick={logClick('ConsumerHealthPrivacyPolicyClicks', () => {})}
          aria-label={t('OfficeShellPanel.PrivacySettings.ConsumerHealthPrivacyPolicyAriaLabel')}
        >
          {t('OfficeShellPanel.PrivacySettings.ConsumerHealthPrivacyPolicy')}
        </ActionButton>
      </Section>
    ),
    [t]
  );

  return (
    <Stack tokens={containerTokens}>
      {manageMyInfoSection}
      {downloadMyDataSection}
      {isEnglishUSLocale() && consumerHealthPrivacyPolicySection}
    </Stack>
  );
};

export const PrivacySettingsPanel = (props: IOfficeShellPanelProps<'PrivacySettings'>) => {
  const { t } = useLocalizedStrings();
  const { closeButtonAriaLabel, onDismiss, ...otherProps } = props;

  return (
    <Panel
      isBlocking
      isLightDismiss
      onDismiss={onDismiss}
      closeButtonAriaLabel={closeButtonAriaLabel}
      styles={{ content: { paddingLeft: 24, paddingRight: 24 } }}
      headerText={t('OfficeShellPanel.PrivacySettings.Header')}
      {...otherProps}
    >
      <PrivacySettings />
    </Panel>
  );
};
